/**
 * Benchmark del profesional vs promedio de su clínica (mismo periodo).
 * Ver docs/PROFESSIONALS_DETAIL_BENCHMARK.md
 */

export type ClinicBenchmarkPeerRow = {
  profesionalId: number;
  ingresos: number;
  turnosAtendidos: number;
};

export type ClinicBenchmarkMetric = {
  self: number;
  average: number;
  /** null si el promedio de peers es 0 (evita NaN/Infinity). */
  deltaPct: number | null;
};

export type ClinicBenchmarkResult = {
  /** Peers usados en el promedio (excluye al profesional actual). */
  peerCount: number;
  /** Sin filas de clínica (API vacía / fallida). */
  noClinicData: boolean;
  /** Clínica con 0 peers (p. ej. 1 solo profesional activo). */
  insufficientBase: boolean;
  ingresos: ClinicBenchmarkMetric | null;
  turnos: ClinicBenchmarkMetric | null;
};

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function roundMoney(value: number): number {
  return Math.round(value * 100) / 100;
}

function peerAverage(values: number[]): number {
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

function deltaPct(self: number, average: number): number | null {
  if (average === 0) return null;
  return round1(((self - average) / average) * 100);
}

/**
 * Promedio simple de peers (excluye al profesional).
 * Universo: profesionales con actividad en el mes (misma base que el ranking).
 */
export function computeClinicBenchmark(input: {
  profesionalId: number;
  rows: ClinicBenchmarkPeerRow[];
}): ClinicBenchmarkResult {
  const { profesionalId, rows } = input;

  if (rows.length === 0) {
    return {
      peerCount: 0,
      noClinicData: true,
      insufficientBase: true,
      ingresos: null,
      turnos: null,
    };
  }

  const peers = rows.filter((row) => row.profesionalId !== profesionalId);
  const self = rows.find((row) => row.profesionalId === profesionalId) ?? null;

  if (peers.length === 0) {
    return {
      peerCount: 0,
      noClinicData: false,
      insufficientBase: true,
      ingresos: null,
      turnos: null,
    };
  }

  if (!self) {
    return {
      peerCount: peers.length,
      noClinicData: false,
      insufficientBase: false,
      ingresos: null,
      turnos: null,
    };
  }

  const avgIngresos = peerAverage(peers.map((p) => p.ingresos));
  const avgTurnos = peerAverage(peers.map((p) => p.turnosAtendidos));

  return {
    peerCount: peers.length,
    noClinicData: false,
    insufficientBase: false,
    ingresos: {
      self: self.ingresos,
      average: roundMoney(avgIngresos),
      deltaPct: deltaPct(self.ingresos, avgIngresos),
    },
    turnos: {
      self: self.turnosAtendidos,
      average: round1(avgTurnos),
      deltaPct: deltaPct(self.turnosAtendidos, avgTurnos),
    },
  };
}
